import React from "react";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import { BLANKS, CONDITION_OPS, conditionSummary, evalCondition, sortDistinctValues } from "./columnFilters";

/**
 * Excel-style column filter dropdown, opened from a header's funnel button.
 *
 * Layout mirrors Excel's AutoFilter menu:
 *   Sort A→Z / Z→A
 *   Clear Filter From "<column>"
 *   Text|Number|Date Filters ▸  (condition ops; opens an inline value editor)
 *   [search]
 *   (Select All) + distinct-value checklist
 *   OK / Cancel
 *
 * Props:
 *   colKey, label, type      — column identity + filter type
 *   values                   — [{ value, iso?, count }] distinct values (unsorted, no blanks entry)
 *   hasBlanks                — whether any row is blank in this column
 *   filter                   — current { selected, condition } or null
 *   sortDir                  — "asc" | "desc" | null (current grid sort on this column)
 *   anchor                   — { left, top } fixed-position coordinates
 *   onSort(colKey, dir), onApply(colKey, filterOrNull), onClose()
 */
class ColumnFilterMenu extends WComponent {
	constructor(props) {
		super(props);
		const f = props.filter || {};
		const cond = f.condition && f.condition.op ? f.condition : null;
		this.state = {
			search: "",
			// Checklist state — Set of display values (BLANKS sentinel included).
			// Mirrors the filter currently in effect: with a condition active the
			// values passing it show checked; with a checklist selection, those
			// values; otherwise everything (no filter).
			checked: this._initialChecked(props, f, cond),
			showCondOps: false,
			editingOp: null, // op def being edited in the inline value editor
			v1: cond ? cond.v1 || "" : "",
			v2: cond ? cond.v2 || "" : "",
		};
	}

	_initialChecked(props, f, cond) {
		if (cond) {
			const passing = props.values
				.filter(v => evalCondition(props.type, cond, v.value, v.iso || null))
				.map(v => v.value);
			return new Set(passing);
		}
		if (Array.isArray(f.selected)) return new Set(f.selected);
		return new Set(this._allTokens(props));
	}

	_allTokens(props) {
		const p = props || this.props;
		const tokens = p.values.map(v => v.value);
		if (p.hasBlanks) tokens.push(BLANKS);
		return tokens;
	}

	_sortLabels() {
		const { type } = this.props;
		if (type === "number") return ["Sort Smallest to Largest", "Sort Largest to Smallest"];
		if (type === "date") return ["Sort Oldest to Newest", "Sort Newest to Oldest"];
		return ["Sort A to Z", "Sort Z to A"];
	}

	_condSectionLabel() {
		const { type } = this.props;
		if (type === "number") return "Number Filters";
		if (type === "date") return "Date Filters";
		return "Text Filters";
	}

	// Values matching the search box (checklist rows shown).
	_visibleValues() {
		const q = (this.state.search || "").trim().toLowerCase();
		const sorted = sortDistinctValues(this.props.values, this.props.type);
		const list = sorted.map(v => ({ token: v.value, label: v.value, count: v.count }));
		if (this.props.hasBlanks) {
			const blankCount = this.props.blanksCount;
			list.push({ token: BLANKS, label: BLANKS, count: blankCount, blank: true });
		}
		if (!q) return list;
		return list.filter(v => String(v.label).toLowerCase().indexOf(q) !== -1);
	}

	onSort = dir => {
		this.props.onSort(this.props.colKey, dir);
		this.props.onClose();
	};

	onClear = () => {
		this.props.onApply(this.props.colKey, null);
		this.props.onClose();
	};

	toggleCondOps = () => {
		this.update({ showCondOps: !this.state.showCondOps, editingOp: null });
	};

	onPickOp = def => {
		const cond = (this.props.filter && this.props.filter.condition) || null;
		const keep = cond && cond.op === def.op;
		this.update({
			editingOp: def,
			v1: keep ? cond.v1 || "" : "",
			v2: keep ? cond.v2 || "" : "",
		});
	};

	// A condition REPLACES any checklist selection (Excel behavior — a custom
	// filter and a value checklist are alternative filter modes, never ANDed).
	onApplyCondition = () => {
		const { editingOp, v1, v2 } = this.state;
		if (!editingOp || v1 === "" || (editingOp.two && v2 === "")) return;
		const next = {
			selected: null,
			condition: { op: editingOp.op, v1, v2: editingOp.two ? v2 : null },
		};
		this.props.onApply(this.props.colKey, next);
		this.props.onClose();
	};

	onClearCondition = () => {
		this.props.onApply(this.props.colKey, null);
		this.update({
			editingOp: null,
			showCondOps: false,
			v1: "",
			v2: "",
			checked: new Set(this._allTokens(this.props)),
		});
	};

	toggleValue = token => {
		const checked = new Set(this.state.checked);
		if (checked.has(token)) checked.delete(token);
		else checked.add(token);
		this.update({ checked });
	};

	// (Select All) toggles the values currently visible under the search.
	toggleSelectAll = () => {
		const visible = this._visibleValues().map(v => v.token);
		const checked = new Set(this.state.checked);
		const allOn = visible.every(t => checked.has(t));
		visible.forEach(t => (allOn ? checked.delete(t) : checked.add(t)));
		this.update({ checked });
	};

	// OK commits the checklist as the column's filter, REPLACING any condition
	// (mirror of onApplyCondition — the two modes never combine). All values
	// checked = no filter at all.
	onOk = () => {
		const all = this._allTokens();
		const checked = this.state.checked;
		const selected = all.filter(t => checked.has(t));
		if (!selected.length) return; // nothing checked would hide every row — OK is disabled
		const next = selected.length === all.length ? null : { selected, condition: null };
		this.props.onApply(this.props.colKey, next);
		this.props.onClose();
	};

	onSearchChange = e => {
		this.update({ search: e.target.value });
	};

	renderConditionEditor() {
		const { editingOp, v1, v2 } = this.state;
		const { type } = this.props;
		const inputType = type === "date" ? "date" : type === "number" ? "number" : "text";
		const canApply = v1 !== "" && (!editingOp.two ? true : v2 !== "");
		return (
			<div className="et-cfm__cond-editor">
				<div className="et-cfm__cond-editor-title">{editingOp.label.replace("…", "")}</div>
				<input
					className="et-cfm__cond-input"
					type={inputType}
					autoFocus
					value={v1}
					placeholder="Value"
					onChange={e => this.update({ v1: e.target.value })}
					onKeyDown={e => {
						if (e.key === "Enter") this.onApplyCondition();
					}}
				/>
				{editingOp.two && (
					<>
						<div className="et-cfm__cond-and">and</div>
						<input
							className="et-cfm__cond-input"
							type={inputType}
							value={v2}
							placeholder="Value"
							onChange={e => this.update({ v2: e.target.value })}
							onKeyDown={e => {
								if (e.key === "Enter") this.onApplyCondition();
							}}
						/>
					</>
				)}
				<div className="et-cfm__cond-editor-actions">
					<button className="et-btn et-btn--ghost et-btn--xs" onClick={() => this.update({ editingOp: null })}>
						Back
					</button>
					<button className="et-btn et-btn--primary et-btn--xs" onClick={this.onApplyCondition} disabled={!canApply}>
						Apply
					</button>
				</div>
			</div>
		);
	}

	renderConditionSection() {
		const { type, filter } = this.props;
		const ops = CONDITION_OPS[type] || [];
		if (!ops.length) return null;
		const cond = filter && filter.condition && filter.condition.op ? filter.condition : null;
		const { showCondOps, editingOp } = this.state;

		return (
			<div className="et-cfm__section">
				<button className="et-cfm__row et-cfm__row--submenu" onClick={this.toggleCondOps}>
					<Icon.Sliders size={12} className="et-cfm__row-icon" />
					<span>{this._condSectionLabel()}</span>
					{cond && <span className="et-cfm__cond-badge" title={conditionSummary(type, cond)}>1</span>}
					<Icon.ChevronRight size={12} className={"et-cfm__row-chevron" + (showCondOps ? " et-cfm__row-chevron--open" : "")} />
				</button>
				{showCondOps && !editingOp && (
					<div className="et-cfm__ops">
						{ops.map(def => (
							<button
								key={def.op}
								className={"et-cfm__row et-cfm__row--op" + (cond && cond.op === def.op ? " et-cfm__row--op-active" : "")}
								onClick={() => this.onPickOp(def)}
							>
								{def.label}
							</button>
						))}
						{cond && (
							<button className="et-cfm__row et-cfm__row--op et-cfm__row--op-clear" onClick={this.onClearCondition}>
								Clear condition ({conditionSummary(type, cond)})
							</button>
						)}
					</div>
				)}
				{showCondOps && editingOp && this.renderConditionEditor()}
			</div>
		);
	}

	render() {
		const { label, anchor, sortDir, filter, onClose } = this.props;
		const [ascLabel, descLabel] = this._sortLabels();
		const visible = this._visibleValues();
		const checked = this.state.checked;
		const allVisibleChecked = visible.length > 0 && visible.every(v => checked.has(v.token));
		const anyChecked = this._allTokens().some(t => checked.has(t));
		const hasActive = !!(filter && (Array.isArray(filter.selected) || (filter.condition && filter.condition.op)));

		return (
			<>
				<div className="et-cfm__backdrop" onClick={onClose} onMouseDown={e => e.stopPropagation()} />
				<div
					className="et-cfm"
					style={{ left: anchor.left, top: anchor.top }}
					onMouseDown={e => e.stopPropagation()}
					onClick={e => e.stopPropagation()}
				>
					<div className="et-cfm__section">
						<button
							className={"et-cfm__row" + (sortDir === "asc" ? " et-cfm__row--active" : "")}
							onClick={() => this.onSort("asc")}
						>
							<Icon.ArrowUp size={12} className="et-cfm__row-icon" />
							<span>{ascLabel}</span>
							{sortDir === "asc" && <Icon.Check size={12} className="et-cfm__row-check" />}
						</button>
						<button
							className={"et-cfm__row" + (sortDir === "desc" ? " et-cfm__row--active" : "")}
							onClick={() => this.onSort("desc")}
						>
							<Icon.ArrowDown size={12} className="et-cfm__row-icon" />
							<span>{descLabel}</span>
							{sortDir === "desc" && <Icon.Check size={12} className="et-cfm__row-check" />}
						</button>
					</div>

					<div className="et-cfm__section">
						<button className="et-cfm__row" onClick={this.onClear} disabled={!hasActive}>
							<Icon.XCircle size={12} className="et-cfm__row-icon" />
							<span>Clear Filter From &quot;{label}&quot;</span>
						</button>
					</div>

					{this.renderConditionSection()}

					<div className="et-cfm__section et-cfm__section--values">
						<div className="et-cfm__search">
							<Icon.Search size={12} className="et-cfm__search-icon" />
							<input
								className="et-cfm__search-input"
								type="text"
								placeholder="Search"
								value={this.state.search}
								onChange={this.onSearchChange}
							/>
						</div>
						<div className="et-cfm__values">
							<label className="et-cfm__value et-cfm__value--all">
								<input type="checkbox" checked={allVisibleChecked} onChange={this.toggleSelectAll} />
								<span>(Select All)</span>
							</label>
							{visible.map(v => (
								<label key={v.token} className={"et-cfm__value" + (v.blank ? " et-cfm__value--blank" : "")}>
									<input type="checkbox" checked={checked.has(v.token)} onChange={() => this.toggleValue(v.token)} />
									<span className="et-cfm__value-label" title={v.label}>
										{v.label}
									</span>
									<span className="et-cfm__value-count">{v.count}</span>
								</label>
							))}
							{visible.length === 0 && <div className="et-cfm__no-values">No matching values</div>}
						</div>
					</div>

					<div className="et-cfm__footer">
						<button className="et-btn et-btn--ghost et-btn--xs" onClick={onClose}>
							Cancel
						</button>
						<button
							className="et-btn et-btn--primary et-btn--xs"
							onClick={this.onOk}
							disabled={!anyChecked}
							title={anyChecked ? undefined : "Check at least one value"}
						>
							OK
						</button>
					</div>
				</div>
			</>
		);
	}
}

export default ColumnFilterMenu;
